import { useState, useCallback, useMemo, type ReactNode } from 'react';
import { useTranslation } from '@mukil/i18n';
import { cn } from '../../lib/cn';

export interface InfoBannerProps {
  /** Optional heading text displayed in bold above children. */
  title?: string;
  /** Banner body content. */
  children: ReactNode;
  /** When true, renders a dismiss (X) button. Requires `storageKey`. */
  dismissible?: boolean;
  /** Called when the user dismisses the banner. */
  onDismiss?: () => void;
  /**
   * localStorage key used to persist dismissal state.
   * Required when `dismissible` is true so re-renders stay dismissed.
   *
   * The key may change over the banner's lifetime (e.g. a consumer that
   * derives it from data that resolves after mount); the dismissed state is
   * re-read for the new key, so the banner shows/hides according to the key
   * it is CURRENTLY rendered with — never a stale read from the first key.
   */
  storageKey?: string;
  /** Additional CSS classes applied to the outer container. */
  className?: string;
  /**
   * Semantic tone. `'info'` (default) is the original neutral/brand callout.
   * `'success'` swaps the icon/border/text to the success token family
   * (`--color-success`/`--color-alert-success-text`) for a positive,
   * "all clear" state — e.g. "ready to file" — instead of hand-rolling a
   * separate success-coloured banner alongside this component (the two
   * WILL drift; see docs/design-system/CLAUDE.md's "Rolling a bespoke
   * primitive" adversarial-review failure mode).
   */
  variant?: 'info' | 'success';
  /**
   * Set when the banner floats over page content (a fixed/sticky toast-like
   * placement) instead of sitting inline in the layout. The alert fills are
   * light tints made for inline callouts, so over content they read as
   * see-through. `floating` puts the banner on an opaque card surface
   * (`--color-bg-card`: white, or the dark card colour in dark mode) with an
   * `--elevation-3` shadow; `className` then applies to that surface, the
   * outermost element.
   */
  floating?: boolean;
}

function IconInfo({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}

function IconCheckCircle({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

function IconX({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

/**
 * Dismiss button box: a 44x44 touch target on a coarse (touch) pointer,
 * whatever the viewport width (landscape phones, tablets), and the original
 * compact 20x20 with a mouse. On touch the negative margin cancels the extra
 * size, so the icon sits exactly where it did and the banner's height is
 * unchanged: at `-m-3` the hit area reaches the banner's `py-3` edge.
 */
const DISMISS_HIT_AREA =
  'flex h-5 w-5 shrink-0 items-center justify-center rounded pointer-coarse:-m-3 pointer-coarse:h-11 pointer-coarse:w-11';

function is_dismissed(key: string | undefined): boolean {
  if (!key) return false;
  try {
    return localStorage.getItem(key) === 'dismissed';
  } catch {
    return false;
  }
}

function persist_dismiss(key: string | undefined): void {
  if (!key) return;
  try {
    localStorage.setItem(key, 'dismissed');
  } catch {
    // localStorage may be unavailable (private browsing, quota exceeded)
  }
}

export function InfoBanner({
  title,
  children,
  dismissible = false,
  onDismiss,
  storageKey,
  className,
  variant = 'info',
  floating = false,
}: InfoBannerProps) {
  const { t } = useTranslation('common');
  // Persisted dismissal, re-read whenever the key changes. Reading it only on
  // mount (the previous `useState(() => is_dismissed(storageKey))`) froze the
  // answer for the FIRST key: a consumer whose key changes after mount would
  // keep the stale result, and a dismissal written under one key would never
  // be found under the other. Generic hardening.
  const stored_dismissed = useMemo(
    () => is_dismissed(storageKey),
    [storageKey],
  );
  // In-session dismissal, remembered against the key it was made under. It
  // covers keyless banners and a storage write that failed (private mode,
  // quota), without leaking onto a different key the banner moves to.
  const [session_dismissed, set_session_dismissed] = useState<{
    key: string | undefined;
  } | null>(null);
  const hidden =
    stored_dismissed ||
    (session_dismissed !== null && session_dismissed.key === storageKey);

  const handle_dismiss = useCallback(() => {
    set_session_dismissed({ key: storageKey });
    persist_dismiss(storageKey);
    onDismiss?.();
  }, [storageKey, onDismiss]);

  if (hidden) return null;

  const is_success = variant === 'success';
  const Icon = is_success ? IconCheckCircle : IconInfo;

  const banner = (
    <div
      role="status"
      className={cn(
        is_success
          ? 'alert-success border-success'
          : 'alert-info border-primary',
        'flex items-start gap-3 rounded-xl border-s-4 px-4 py-3',
        floating ? undefined : className,
      )}
    >
      <Icon
        className={cn(
          'mt-0.5 h-5 w-5 shrink-0',
          is_success ? 'text-success' : 'text-primary',
        )}
      />
      <div className="min-w-0 flex-1">
        {title ? (
          <p
            className={cn(
              'text-sm font-semibold',
              is_success
                ? 'text-[color:var(--color-alert-success-text)]'
                : 'text-primary-dark',
            )}
          >
            {title}
          </p>
        ) : null}
        <div
          className={cn(
            'text-sm',
            is_success
              ? 'text-[color:var(--color-alert-success-text)]'
              : 'text-primary-dark',
          )}
        >
          {children}
        </div>
      </div>
      {dismissible ? (
        <button
          type="button"
          aria-label={t('dismiss')}
          onClick={handle_dismiss}
          className={cn(
            DISMISS_HIT_AREA,
            is_success
              ? 'text-success hover:text-[color:var(--color-alert-success-text)]'
              : 'text-primary hover:text-primary-dark',
          )}
        >
          <IconX className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );

  if (!floating) return banner;

  return (
    <div
      data-surface="floating"
      className={cn(
        'rounded-xl bg-[var(--color-bg-card)] shadow-[var(--elevation-3)]',
        className,
      )}
    >
      {banner}
    </div>
  );
}
